import React, { useEffect, useState } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input, Select } from "../../../../../../../../components/Form";
import SelectMultiple, {
  SelectMultipleCount,
} from "../../../../../../../../components/Form/SelectMultiple";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import { Office } from "../../../../../../../../models/office";
import { Port } from "../../../../../../../../models/port";
import { Status } from "../../../../../../../../util/enums";
import { useOfficePageContext } from "../../context/Office";
import { ModalCustom, ModalSection, PortQuantityBadgeContainer } from "./styles";

interface Props {
  title?: string;
  officeToUpdate?: Office;
  hasError?: boolean;
  isOpened: boolean;
  isLoading: boolean;
  onCloseModal: () => void;
  onSubmit: (office: Office) => void;
  isView?: boolean;
}

const RoomModal = ({
  title,
  isOpened,
  officeToUpdate,
  hasError,
  isLoading,
  onCloseModal,
  onSubmit,
  isView,
}: Props) => {
  const { t: translate } = useTranslation();
  const { countryList, loading: loadingResources, portList } = useOfficePageContext();

  const [name, setName] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [ports, setPorts] = useState<Port[]>([]);
  const [status, setStatus] = useState<string>(Status.ACTIVE);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const disabled = isView || isLoading;

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (roomName) =>
        roomName ? roomName?.length > 0 && roomName !== "" : false
      ),
    code: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    ports: yup
      .array()
      .min(1, translate("selectAtLeastOnePort"))
      .required(translate("thisFieldIsRequired")),
  });

  const clearFields = () => {
    setName("");
    setCode("");
    setCountry("");
    setPorts([]);
    setStatus(Status.ACTIVE);
  };

  const handleValidateSuccess = (data?: Office): boolean => {
    schema.validateSync(
      {
        name: data?.name,
        code: data?.beCode,
        country: data?.countryId,
        ports: data?.ports,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidateError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setErrors(error?.inner);

    return false;
  };

  const isValid = (data?: Office) => {
    try {
      return handleValidateSuccess(data);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const mountData = () => {
    return {
      ...officeToUpdate,
      name,
      beCode: code,
      countryId: country && !Number.isNaN(country) ? Number(country) : null,
      ports: ports || null,
      status,
    } as Office;
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const data = mountData();

    if (isValid(data)) {
      onSubmit(data);
    }
  };

  useEffect(() => {
    if (officeToUpdate) {
      setName(officeToUpdate.name);
      setCode(officeToUpdate?.beCode || "");
      setCountry(String(officeToUpdate?.countryId) || "");
      setPorts(officeToUpdate?.ports || []);
      setStatus(officeToUpdate?.status || "");
    }
  }, [officeToUpdate]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      onCloseModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      isValid(mountData());
    }
  }, [hasFormBeenSubmitted, officeToUpdate, name, code, country, ports]);

  return (
    <ModalCustom
      data-cy="manageOfficeodal"
      open={isOpened}
      handleClose={onCloseModal}
      title={title || (officeToUpdate ? translate("editOffice") : translate("createNewOffice"))}
      dataCyTitle="manageOfficeModalTitle"
      showActionButtons
      actionButtonProps={{
        onCancel: () => {
          clearFields();
          onCloseModal();
        },
        dataCySave: "submitButton",
        dataCyCancel: "cancelButton",
        textSave: officeToUpdate ? translate("update") : translate("create"),
        loading: isLoading,
        onSave: handleSubmit,
        cancelDisabled: isLoading,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        disabled,
        label: translate("officeStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <ModalSection>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={disabled}
              name="code"
              label={translate("beCode")}
              placeholder={translate("code")}
              error={!!(getErrorByField("code") && getErrorByField("code") !== "")}
              errorMessage={getErrorByField("code")}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              disabled={disabled}
              name="name"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!(getErrorByField("name") && getErrorByField("name") !== "")}
              errorMessage={getErrorByField("name")}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Grid>
        </Grid>

        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6} container direction="row">
            <SkeletonComponent isLoading={loadingResources}>
              <Select
                disabled={disabled}
                name="country"
                label={translate("country")}
                placeholder={translate("country")}
                error={!!(getErrorByField("country") && getErrorByField("country") !== "")}
                errorMessage={getErrorByField("country")}
                value={country}
                onChange={(event: SelectChangeEvent<any>) => setCountry(event.target.value)}
              >
                {countryList.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </SkeletonComponent>
          </Grid>

          <Grid item md={6} lg={6} container direction="row" spacing={1}>
            <Grid item xs={11} sm={11}>
              <SkeletonComponent isLoading={loadingResources}>
                <SelectMultiple
                  loading={false}
                  label={translate("ports")}
                  dataCy="selectPorts"
                  disabled={disabled || loadingResources}
                  error={!!(getErrorByField("ports") && getErrorByField("ports") !== "")}
                  errorMessage={getErrorByField("ports")}
                  name="portList"
                  onChange={(event: SelectChangeEvent<any>) => {
                    if (event.target.value.includes(0) || event.target.value.includes(-1)) return;
                    const portsSelecteds = portList?.filter((port) =>
                      event.target.value.includes(port.id)
                    ) as Port[];

                    setPorts(portsSelecteds);
                  }}
                  value={ports.map((port) => Number(port?.id)) || []}
                  onAddAll={() => {
                    setPorts(portList);
                  }}
                  onRemoveAll={() => {
                    setPorts([]);
                  }}
                  renderInternalCount={false}
                  items={portList.map((port) => ({
                    key: Number(port.id),
                    label: port.siteCode,
                  }))}
                  selecteds={
                    ports?.map((port) => ({
                      key: Number(port.id),
                      label: port.siteCode,
                    })) || []
                  }
                />
              </SkeletonComponent>
            </Grid>
            <PortQuantityBadgeContainer item xs={1} sm={1} md={1} lg={1}>
              <SelectMultipleCount
                items={portList.map((port) => ({
                  key: Number(port.id),
                  label: port.siteCode,
                }))}
                selecteds={
                  ports?.map((port) => ({
                    key: Number(port.id),
                    label: port.siteCode,
                  })) || []
                }
              />
            </PortQuantityBadgeContainer>
          </Grid>
        </Grid>
      </ModalSection>
    </ModalCustom>
  );
};

export default RoomModal;
